import AndroidIcon from '@mui/icons-material/Android'
import AppleIcon from '@mui/icons-material/Apple'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined'
import LaunchIcon from '@mui/icons-material/Launch'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import PinOutlinedIcon from '@mui/icons-material/PinOutlined'
import PreviewIcon from '@mui/icons-material/Preview'
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import {
  Alert,
  AlertTitle,
  AppBar,
  Avatar,
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  DividerProps,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Modal,
  Slide,
  Snackbar,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import {
  FC,
  forwardRef,
  ReactElement,
  Ref,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import Draggable from 'react-draggable'
import { useTranslation } from 'react-i18next'

import { useGetDocuments } from '../../hooks/api/documents/useGetDocuments'
import { useGetDriverProfile } from '../../hooks/api/drivers/useGetDriverProfile'
import { useGetFileUrl } from '../../hooks/useGetFileUrl'
import { getInsuranceFromS3 } from '../../hooks/useS3Storage'
import {
  DocumentItem,
  DriverData,
  SelectedVehicle,
  Vehicle,
} from '../../types/interfaces'
import ProfileUpload from '../components/forms/ProfileUploadWrapper'
import SubmitButton from '../components/forms/SubmitButtonWrapper'
import ProfileSkeleton from '../components/loading/Profile'
import { a11yProps } from '../components/navigation/a11yProps'
import {
  StyledTab,
  StyledTabList,
  TabPanel,
} from '../components/navigation/TabNav'
import ProfileResources from '../components/ProfileResources'
import { profilePictureSchema } from '../schema/input'
import {
  ProfileAvatarType,
  ResourceType,
  SnackBarAlertType,
  UpdateProfilePicturePayload,
} from '../types/interface'
import { getDriverProvince, updateDriverInfo } from '../utils/driver-profile'
import { pdfUrltoImage } from '../utils/pdf-document'
import {
  CertificateDialog,
  CertificatePage,
  NBDriverIDPage,
} from './Certificates'

const KamloopsDocs = [
  {
    fileKey: 'ground_transportation_waiting_area.jpg',
    name: 'Airport Waiting Area',
    type: 'JPG',
  },
  {
    fileKey: 'ground_transportation_map_2022.jpg',
    name: 'Airport Parking Area',
    type: 'JPG',
  },
]

const KelownaDocs = [
  {
    fileKey: 'ylw_atb_curb_operating_map.png',
    name: 'Airport Pick-up/Waiting area',
    type: 'PNG',
  },
]

const BRITISH_COLUMBIA_PROVINCE_ID = 2
const NEW_BRUNSWICK_PROVINCE_ID = 3

const ProfileAvatar: FC<ProfileAvatarType> = ({ name, profile, size = 64 }) => {
  const profileAvatarUrl = useGetFileUrl(profile)

  const initials = name
    .split(/\s/)
    .reduce((response, word) => response + word.slice(0, 1), '')

  const avatarUrl = useMemo(
    () =>
      profileAvatarUrl
        ? `${import.meta.env.VITE_TRAINING_GUIDE_CLOUDFRONT_URL}${profileAvatarUrl}`
        : '',
    [profileAvatarUrl]
  )

  const avatarConfig = {
    src: avatarUrl,
    sx: {
      fontWeight: 700,
      height: size,
      width: size,
    },
  }

  return <Avatar {...avatarConfig}>{initials}</Avatar>
}

const Transition = forwardRef(
  (
    props: {
      children: ReactElement
    } & TransitionProps,
    ref: Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
)

interface DialogState {
  avatar: boolean
  id: boolean
}

const AvatarDialog = ({
  isLargeScreen,
  open,
  refetch,
  setAlertEvent,
  setOpen,
}: {
  isLargeScreen: boolean
  open: DialogState
  refetch: () => void
  setAlertEvent: (alertEvent: SnackBarAlertType) => void
  setOpen: (open: DialogState) => void
}) => {
  const { t } = useTranslation() // Constant that will works as translation function

  const initialValues = {
    picture: '',
  }

  const submitConfig = {
    ...(isLargeScreen
      ? {
          sx: {
            width: '30%',
          },
        }
      : {
          fullWidth: true,
        }),
  }

  const gridContainerConfig = {
    container: true,
    justifyContent: 'center',
    sx: { mb: 8, p: 2 },
  }

  const gridItemConfig = {
    item: true,
    xs: 12,
  }

  const boxConfig: BoxProps = {
    sx: {
      backgroundColor: '#FFF',
      bottom: 0,
      display: 'flex',
      left: 0,
      p: 1,
      position: 'fixed',
      right: 0,
      zIndex: 1000,
      ...(isLargeScreen && { justifyContent: 'center' }),
    },
  }

  const handleSubmit = async (values: UpdateProfilePicturePayload) => {
    // Handle form submit
    let returnedValue: boolean = false
    await updateDriverInfo(values).then((response: boolean) => {
      returnedValue = response
    })
    return returnedValue
  }

  return (
    <Dialog fullScreen open={open.avatar} TransitionComponent={Transition}>
      <AppBar color="dense" sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            aria-label="close"
            color="inherit"
            edge="start"
            onClick={() => setOpen({ ...open, avatar: false })}
          >
            <CloseIcon />
          </IconButton>
          <Typography component="div" sx={{ flexGrow: 1 }} variant="h6">
            {t('profile.personal.profile_picture')}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          justifyContent: 'center',
          pb: 10,
        }}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting }) => {
            const isSuccess = await handleSubmit(values)
            setSubmitting(false)
            if (!isSuccess) {
              // Save rejected (e.g. 502 from AF sync): show an error and keep the dialog open
              // instead of a false success.
              setAlertEvent({
                message: `${t('documents.alerts.error')}`,
                open: true,
                type: 'error',
              })
              return
            }
            setAlertEvent({
              message: `${t('profile.status.photo_updated_successfully')}`,
              open: true,
              type: 'success',
            })
            setOpen({ ...open, avatar: false })
            refetch()
          }}
          validationSchema={profilePictureSchema}
        >
          <Form>
            <Grid {...gridContainerConfig}>
              <Grid
                item
                md={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
                xs={12}
              >
                <Grid {...gridItemConfig}>
                  <Typography align="center" gutterBottom variant="body1">
                    {t('profile.personal.upload_photo_title')}
                  </Typography>
                </Grid>
                <Grid {...gridItemConfig}>
                  <ProfileUpload folder="Profile" name="picture" />
                </Grid>
              </Grid>
            </Grid>
            <Box {...boxConfig}>
              <SubmitButton {...submitConfig}>
                {t('profile.personal.upload_photo')}
              </SubmitButton>
            </Box>
          </Form>
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

const IdDialog = ({
  data,
  open,
  setOpen,
}: {
  data: DriverData
  open: DialogState
  setOpen: (open: DialogState) => void
}) => {
  const [driverProvince, setDriverProvince] = useState<number>(0)
  const [pinkSlip, setPinkSlip] = useState<string[]>([])
  const [pinkSlipLoading, setPinkSlipLoading] = useState(false)
  const [pinkSlipError, setPinkSlipError] = useState(false)
  const [show, setShow] = useState<boolean>(false)
  const [zoom, setZoom] = useState(1)
  const [isRCCModalOpen, setRCCModalOpen] = useState<boolean>(false)
  const [isNBDriverIDModalOpen, setNBDriverIDModalOpen] =
    useState<boolean>(false)
  const [selectedVehicle, setSelectedVehicle] = useState<null | SelectedVehicle>(null)
  const [expirationDate, setExpirationDate] = useState<null | string>(null)
  const [isBCProvince, setIsBCProvince] = useState<boolean>(false)
  const [isNBProvince, setIsNBProvince] = useState<boolean>(false)
  const { data: documentsData } = useGetDocuments()
  const theme = useTheme()
  const { t } = useTranslation()

  const getPinkSlip = useCallback(async () => {
    setPinkSlipLoading(true)
    setPinkSlipError(false)
    try {
      const s3PinkSlip = await getInsuranceFromS3(data?.city?.province_id)
      if (s3PinkSlip) {
        const s3PinkSlipUrl = await pdfUrltoImage(s3PinkSlip)
        setPinkSlip(s3PinkSlipUrl)
      }
    } catch {
      setPinkSlipError(true)
    } finally {
      setPinkSlipLoading(false)
    }
  }, [data?.city?.province_id])

  useEffect(() => {
    getDriverProvince(data.city.id).then((response: number) => {
      setDriverProvince(response)
    })
  }, [data.city.id])

  useEffect(() => {
    if (documentsData?.user_documents) {
      const backgroundCheckDoc = documentsData.user_documents.find(
        (doc: DocumentItem) => doc.document?.name === 'background'
      )
      const expiration = backgroundCheckDoc?.expiration_date || null
      if (expiration) {
        setExpirationDate(expiration)
      }
    }
  }, [documentsData])

  useEffect(() => {
    if (driverProvince) {
      if (driverProvince === BRITISH_COLUMBIA_PROVINCE_ID) {
        setIsBCProvince(true)
      }
      if (driverProvince === NEW_BRUNSWICK_PROVINCE_ID) {
        setIsNBProvince(true)
      }
    }
  }, [driverProvince])

  const handleOpen = async () => {
    setShow(true)
    if (pinkSlip.length === 0 && !pinkSlipLoading) {
      await getPinkSlip()
    }
  }

  const handleClose = () => {
    setShow(false)
    setZoom(1)
  }

  const handleOpenRCCModal = (vehicle: Vehicle) => {
    setRCCModalOpen(true)
    setSelectedVehicle({
      expiration_date: expirationDate,
      full_name: `${data?.first_name ?? ''} ${data?.last_name ?? ''}`,
      profile_picture: data?.profile_picture ?? '',
      vehicle,
    })
  }

  const handleCloseRCCModal = () => {
    setRCCModalOpen(false)
  }

  const handleOpenNBDriverIDModal = (vehicle: Vehicle) => {
    setNBDriverIDModalOpen(true)
    setSelectedVehicle({
      full_name: `${data?.first_name ?? ''} ${data?.last_name ?? ''}`,
      profile_picture: data?.profile_picture ?? '',
      vehicle,
    })
  }

  const handleCloseNBDriverIDModal = () => {
    setNBDriverIDModalOpen(false)
  }

  const handleZoomIn = () => {
    setZoom((prevZoom) => prevZoom + 0.1)
  }

  const handleZoomOut = () => {
    setZoom((prevZoom) => prevZoom - 0.1)
  }

  const gridContainerConfig = {
    container: true,
    justifyContent: 'center',
    sx: { mb: 2, p: 2 },
  }

  const gridItemConfig = {
    item: true,
    xs: 12,
  }

  const listItemConfig = {
    disablePadding: false,
  }

  const divConfig: DividerProps = {
    variant: 'middle',
  }

  const closeButtonStyle = {
    color: theme.palette.grey[500],
  }

  const zoomButtonStyle = {
    color: theme.palette.grey[500],
  }

  const boxConfigStyle = {
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'space-between',
    left: 0,
    minHeight: 56,
    padding: 0.6,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 999,
  }

  return (
    <Dialog fullScreen open={open.id} TransitionComponent={Transition}>
      <AppBar color="dense" sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            aria-label="close"
            color="inherit"
            edge="start"
            onClick={() => setOpen({ ...open, id: false })}
          >
            <CloseIcon />
          </IconButton>
          <Typography component="div" sx={{ flexGrow: 1 }} variant="h6">
            {driverProvince === 2
              ? t('profile.info.record_check_certificate')
              : t('profile.info.driver_id')}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Grid {...gridContainerConfig}>
          <Grid
            item
            md={6}
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'center',
            }}
            xs={12}
          >
            <Grid {...gridItemConfig}>
              <ProfileAvatar
                name={`${data?.first_name} ${data?.last_name}`}
                profile={data.profile_picture}
                size={144}
              />
            </Grid>
            <List sx={{ width: '100%' }}>
              <ListItem {...listItemConfig}>
                <ListItemText
                  primary={
                    <Typography color="text.disabled" variant="subtitle2">
                      {' '}
                      {t('profile.info.issued_by_licensee')}{' '}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1">
                      Uride Technologies Inc.
                    </Typography>
                  }
                />
              </ListItem>
              <Divider {...divConfig} />
              <ListItem {...listItemConfig}>
                <ListItemText
                  primary={
                    <Typography color="text.disabled" variant="subtitle2">
                      {' '}
                      {t('profile.personal.legal_name')}{' '}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1">{`${data?.first_name} ${data?.last_name}`}</Typography>
                  }
                />
              </ListItem>
              <Divider {...divConfig} />
              <ListItem {...listItemConfig}>
                <ListItemText
                  primary={
                    <Typography color="text.disabled" variant="subtitle2">
                      {' '}
                      {t('profile.info.city')}{' '}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body1">{data?.city.name}</Typography>
                  }
                />
              </ListItem>
              <Divider {...divConfig} />
              {isBCProvince && (
                <>
                  <ListItem {...listItemConfig}>
                    <ListItemText
                      primary={
                        <Typography color="text.disabled" variant="subtitle2">
                          {' '}
                          {t(
                            'profile.info.bc_national_safety_code_number'
                          )}{' '}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">202-660-970</Typography>
                      }
                    />
                  </ListItem>
                  <Divider {...divConfig} />
                </>
              )}
              {data?.pink_slip && (
                <ListItemButton onClick={handleOpen}>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{ textTransform: 'capitalize' }}
                        variant="subtitle1"
                      >
                        {t('profile.info.pink_slip')}
                      </Typography>
                    }
                  />
                  <PreviewIcon color="primary" />
                </ListItemButton>
              )}
            </List>
            {isBCProvince && (
              <Grid {...gridItemConfig}>
                <Alert severity="info">
                  <AlertTitle>
                    {' '}
                    {t('profile.info.record_check_certificate')}{' '}
                  </AlertTitle>
                  {t('profile.info.record_check_certificate_text')}
                </Alert>
              </Grid>
            )}
            {data?.vehicles?.map((vehicle: Vehicle) => (
              <List key={vehicle.vehicle_id} sx={{ width: '100%' }}>
                <ListItem {...listItemConfig}>
                  <ListItemText
                    primary={
                      <Typography textAlign="center" variant="h6">
                        {' '}
                        {t('profile.info.vehicle_information')}{' '}
                      </Typography>
                    }
                  />
                </ListItem>
                <Typography variant="subtitle1">
                  {t('profile.menu.vehicle')} {vehicle.plate_number}
                </Typography>
                <ListItem {...listItemConfig}>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.vehicle_make')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {vehicle.make?.name}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider {...divConfig} />
                <ListItem {...listItemConfig}>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.vehicle_model')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {vehicle.model?.name}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider {...divConfig} />
                <ListItem {...listItemConfig}>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.year')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">{vehicle.year}</Typography>
                    }
                  />
                </ListItem>
                <Divider {...divConfig} />
                <ListItem {...listItemConfig}>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.plate_number')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {vehicle.plate_number} ({vehicle.status})
                      </Typography>
                    }
                  />
                </ListItem>
                {isBCProvince && (
                  <ListItem key={vehicle.plate_number}>
                    <Button
                      disabled={vehicle.status !== 'active'}
                      disableElevation
                      onClick={() => handleOpenRCCModal(vehicle)}
                      size="small"
                      startIcon={<BadgeOutlinedIcon />}
                      variant="contained"
                    >
                      {t(`profile.info.view_record_check_certificate`)}
                    </Button>
                  </ListItem>
                )}
                {isNBProvince && (
                  <ListItem key={vehicle.plate_number}>
                    <Button
                      disabled={vehicle.status !== 'active'}
                      disableElevation
                      onClick={() => handleOpenNBDriverIDModal(vehicle)}
                      size="small"
                      startIcon={<BadgeOutlinedIcon />}
                      variant="contained"
                    >
                      {t(`profile.info.view_bc_driver_ID`)}
                    </Button>
                  </ListItem>
                )}
              </List>
            ))}
            <List sx={{ width: '100%' }}>
              <ListItem {...listItemConfig}>
                <ListItemText
                  primary={
                    <Link href="https://uridetech.com" target="_blank">
                      {' '}
                      {t('uride.website')}{' '}
                    </Link>
                  }
                  primaryTypographyProps={{ align: 'center' }}
                />
              </ListItem>
              <Divider {...divConfig} />
              <ListItem {...listItemConfig}>
                <ListItemText
                  primary={
                    <Link href="mailto:recruitment@uridetech.com">
                      recruitment@uridetech.com
                    </Link>
                  }
                  primaryTypographyProps={{ align: 'center' }}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Modal
          onClose={handleClose}
          open={show}
          sx={{
            display: 'flex',
          }}
        >
          <>
            <Box sx={boxConfigStyle}>
              <IconButton onClick={handleClose} sx={closeButtonStyle}>
                <CloseIcon />
              </IconButton>
              <Box>
                <IconButton
                  disabled={zoom >= 2}
                  onClick={handleZoomIn}
                  sx={zoomButtonStyle}
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton
                  disabled={zoom <= 1}
                  onClick={handleZoomOut}
                  sx={zoomButtonStyle}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Box>
            </Box>
            {pinkSlipLoading && (
              <CircularProgress sx={{ margin: 'auto' }} />
            )}
            {pinkSlipError && (
              <Typography sx={{ color: 'error.main', margin: 'auto' }}>
                {t('profile.info.pink_slip_error')}
              </Typography>
            )}
            {!pinkSlipLoading &&
              !pinkSlipError &&
              pinkSlip.map((img) => (
                <Draggable key={img}>
                  <Box sx={{ display: 'block', margin: 'auto' }}>
                    <img
                      alt="pink slip"
                      onDragStart={(e) => e.preventDefault()}
                      src={img}
                      style={{
                        display: 'block',
                        margin: 'auto',
                        maxHeight: '100%',
                        maxWidth: '100%',
                        transform: `scale(${zoom})`,
                      }}
                    />
                  </Box>
                </Draggable>
              ))}
          </>
        </Modal>
        <CertificateDialog
          content={<CertificatePage data={selectedVehicle} />}
          onClose={handleCloseRCCModal}
          open={isRCCModalOpen}
          textToClose={t('common.close')}
          title={t('profile.info.record_check_certificate')}
        />
        <CertificateDialog
          content={<NBDriverIDPage data={selectedVehicle} />}
          onClose={handleCloseNBDriverIDModal}
          open={isNBDriverIDModalOpen}
          textToClose={t('common.close')}
          title={t('profile.info.view_bc_driver_ID')}
        />
      </DialogContent>
    </Dialog>
  )
}

const Profile = () => {
  const { data, error, isLoading, refetch } = useGetDriverProfile() // Fetch driver profile data
  const { i18n, t } = useTranslation() // Get translation function
  const currentLanguage = i18n.language
  const [value, setValue] = useState<number>(0) // Set initial tab value to 0
  const [isRCCModalOpen, setRCCModalOpen] = useState<boolean>(false)
  const [isNBDriverIDModalOpen, setNBDriverIDModalOpen] =
    useState<boolean>(false)
  const [isBCProvince, setIsBCProvince] = useState<boolean>(false)
  const [isNBProvince, setIsNBProvince] = useState<boolean>(false)
  const [selectedVehicle, setSelectedVehicle] = useState<null | SelectedVehicle>(null)
  const [expirationDate, setExpirationDate] = useState<null | string>(null)
  const { data: documentsData } = useGetDocuments()
  const [open, setOpen] = useState<DialogState>({
    // Dialog state manager
    avatar: false,
    id: false,
  })
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  }) // Snackbar state manager
  const theme = useTheme() // MUI theme hook
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md')) // MUI media query hook
  const en_resources = useMemo(
    () => [
      // Driver resources state manager in english
      {
        fileKey: 'uride_driver_terms_and_conditions_ridesharing.pdf',
        name: `${t('profile.info.terms_and_conditions')}`,
        type: 'PDF',
      },
      {
        fileKey: 'driver_app_training.pdf',
        name: `${t('profile.info.driver_app_training')}`,
        type: 'PDF',
      },
    ],
    [t]
  )

  const es_resources = useMemo(
    () => [
      // Driver resources state manager in spanish
      {
        fileKey: 'terminos_condiciones.pdf',
        name: `${t('profile.info.terms_and_conditions')}`,
        type: 'PDF',
      },
      {
        fileKey: 'entrenamiento_app_conductor.pdf',
        name: `${t('profile.info.driver_app_training')}`,
        type: 'PDF',
      },
    ],
    [t]
  )

  const languageResource = useMemo(
    () => (currentLanguage === 'es' ? es_resources : en_resources),
    [currentLanguage, en_resources, es_resources]
  )
  const [driverResources, setDriverResources] =
    useState<ResourceType[]>(languageResource)

  const driverData = data as DriverData

  useEffect(() => {
    setDriverResources(languageResource)
  }, [languageResource])

  useEffect(() => {
    if (documentsData) {
      const backgroundCheckDoc = documentsData.user_documents.find(
        (doc: DocumentItem) => doc.document?.name === 'background'
      )
      const expiration = backgroundCheckDoc
        ? backgroundCheckDoc.expiration_date
        : null
      setExpirationDate(expiration)
    }
  }, [documentsData])

  useEffect(() => {
    if (driverData) {
      getDriverProvince(driverData.city.id).then((provinceId: null | number) => {
        if (provinceId === BRITISH_COLUMBIA_PROVINCE_ID) {
          setIsBCProvince(true)
        }
        if (provinceId === NEW_BRUNSWICK_PROVINCE_ID) {
          setIsNBProvince(true)
        }
      })
    }
  }, [driverData])

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    // Handle tab change
    setValue(newValue)
  }

  const handleClose = (event: Event | SyntheticEvent, reason?: string) => {
    // Close snackbar
    if (reason === 'clickaway') {
      return
    }
    setAlertEvent({ ...alertEvent, open: false })
  }

  const handleOpenRCCModal = (vehicle: Vehicle) => {
    setRCCModalOpen(true)
    setSelectedVehicle({
      expiration_date: expirationDate,
      full_name: `${data?.first_name ?? ''} ${data?.last_name ?? ''}`,
      profile_picture: data?.profile_picture ?? '',
      vehicle,
    })
  }

  const handleCloseRCCModal = () => {
    setRCCModalOpen(false)
  }

  const handleOpenNBDriverIDModal = (vehicle: Vehicle) => {
    setNBDriverIDModalOpen(true)
    setSelectedVehicle({
      full_name: `${data?.first_name ?? ''} ${data?.last_name ?? ''}`,
      profile_picture: data?.profile_picture ?? '',
      vehicle,
    })
  }

  const handleCloseNBDriverIDModal = () => {
    setNBDriverIDModalOpen(false)
  }

  const gridContainerConfig = {
    container: true,
    justifyContent: 'center',
    sx: { mb: 8, p: 2 },
  }

  const gridItemConfig = {
    item: true,
    xs: 12,
  }

  const formatPhone = (phoneNumber: string) =>
    phoneNumber?.replace(/^([+]\d{1})(\d{3})(\d{3})(\d{4})$/g, '$1 ($2)-$3-$4')

  const getAge = () => dayjs().diff(dayjs(driverData?.birth_date), 'year')

  useEffect(() => {
    if (driverData) {
      if (driverData.city?.name === 'Kamloops') {
        const existingResources = driverResources.map(
          (resource) => resource.name
        ) // Get existing resource names
        const filteredResources = KamloopsDocs.filter(
          (resource) => !existingResources.includes(resource.name)
        ) // Filter out existing resources

        if (filteredResources.length > 0) {
          setDriverResources([...driverResources, ...filteredResources])
        }
      }
      if (driverData.city?.name === 'Kelowna') {
        const existingResources = driverResources.map(
          (resource) => resource.name
        ) // Get existing resource names

        const filteredResources = KelownaDocs.filter(
          (resource) => !existingResources.includes(resource.name)
        ) // Filter out existing resources

        if (filteredResources.length > 0) {
          setDriverResources([...driverResources, ...filteredResources])
        }
      }
    }
  }, [driverData, driverResources])

  if (isLoading) {
    return (
      <Grid {...gridContainerConfig}>
        <Grid item md={4} xs={12}>
          <ProfileSkeleton />
        </Grid>
      </Grid>
    )
  }

  if (error) {
    return <div>Error</div>
  }

  return (
    <Grid {...gridContainerConfig}>
      <Grid item md={4} xs={12}>
        <Grid container spacing={2}>
          <Grid {...gridItemConfig}>
            <List>
              <ListItem sx={{ columnGap: 2 }}>
                <ListItemAvatar>
                  <ProfileAvatar
                    name={`${driverData?.first_name} ${driverData?.last_name}`}
                    profile={driverData.profile_picture}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography color="text.primary" variant="subtitle1">
                      {`${driverData?.first_name} ${driverData?.last_name}`}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      color="text.secondary"
                      component="span"
                      variant="body2"
                    >
                      <Stack spacing={2}>
                        <Button
                          onClick={() => setOpen({ ...open, avatar: true })}
                          size="small"
                          variant="outlined"
                        >
                          {t('profile.personal.upload_photo')}
                        </Button>
                        <Button
                          disabled={driverData.status !== 'active'}
                          disableElevation
                          onClick={() => setOpen({ ...open, id: true })}
                          size="small"
                          startIcon={<BadgeOutlinedIcon />}
                          variant="contained"
                        >
                          {t('profile.info.view_driver_id')}
                        </Button>
                      </Stack>
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Grid>
          <Grid {...gridItemConfig}>
            <StyledTabList
              onChange={handleChange}
              value={value}
              variant="fullWidth"
            >
              <StyledTab label={t('profile.menu.personal')} {...a11yProps(0)} />
              <StyledTab label={t('profile.menu.vehicle')} {...a11yProps(1)} />
              <StyledTab
                label={t('profile.menu.resources')}
                {...a11yProps(2)}
              />
            </StyledTabList>
          </Grid>
          <Grid {...gridItemConfig}>
            <TabPanel index={0} value={value}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <EmailOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.company.email')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {driverData?.email}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="inset" />
                <ListItem>
                  <ListItemIcon>
                    <LocalPhoneOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.company.phone')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {formatPhone(driverData?.phone)}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="inset" />
                <ListItem>
                  <ListItemIcon>
                    <CakeOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.personal.birthdate')} |{' '}
                        {t('profile.personal.how_old')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">{`${dayjs(driverData?.birth_date).format('MMMM DD, YYYY')} | ${getAge()} ${t('profile.personal.age')}`}</Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="inset" />
                <ListItem>
                  <ListItemIcon>
                    <FmdGoodOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.city')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {driverData?.city.name}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="inset" />
                <ListItem>
                  <ListItemIcon>
                    <BadgeOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.license_type')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {driverData?.license_type?.name}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="inset" />
                <ListItem>
                  <ListItemIcon>
                    <BadgeOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.license_plate')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {driverData?.license_number}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="inset" />
                <ListItem>
                  <ListItemIcon>
                    <FlagOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {' '}
                        {t('profile.info.member_since')}{' '}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {dayjs(driverData?.created_at).format('MMMM DD, YYYY')}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </TabPanel>
            <TabPanel index={1} value={value}>
              {data?.vehicles?.map((vehicle: Vehicle) => (
                <List key={vehicle.vehicle_id}>
                  <Typography variant="subtitle1">
                    {t('profile.menu.vehicle')} {vehicle.plate_number} (
                    {vehicle.status})
                  </Typography>
                  <ListItem>
                    <ListItemIcon>
                      <DirectionsCarFilledOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography color="text.disabled" variant="subtitle2">
                          {' '}
                          {t('profile.info.vehicle_make')}{' '}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">
                          {vehicle.make?.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider component="li" variant="inset" />
                  <ListItem>
                    <ListItemIcon>
                      <StyleOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography color="text.disabled" variant="subtitle2">
                          {' '}
                          {t('profile.info.vehicle_model')}{' '}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">
                          {vehicle.model?.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider component="li" variant="inset" />
                  <ListItem>
                    <ListItemIcon>
                      <CakeOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography color="text.disabled" variant="subtitle2">
                          {' '}
                          {t('profile.info.year')}{' '}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">{vehicle.year}</Typography>
                      }
                    />
                  </ListItem>
                  <Divider component="li" variant="inset" />
                  <ListItem>
                    <ListItemIcon>
                      <PinOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography color="text.disabled" variant="subtitle2">
                          {' '}
                          {t('profile.info.plate_number')}{' '}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body1">
                          {vehicle.plate_number}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {isBCProvince && (
                    <ListItem key={vehicle.plate_number}>
                      <Button
                        disabled={vehicle.status !== 'active'}
                        disableElevation
                        onClick={() => handleOpenRCCModal(vehicle)}
                        size="small"
                        startIcon={<BadgeOutlinedIcon />}
                        variant="contained"
                      >
                        {t(`profile.info.view_record_check_certificate`)}
                      </Button>
                    </ListItem>
                  )}
                  {isNBProvince && (
                    <ListItem key={vehicle.plate_number}>
                      <Button
                        disabled={vehicle.status !== 'active'}
                        disableElevation
                        onClick={() => handleOpenNBDriverIDModal(vehicle)}
                        size="small"
                        startIcon={<BadgeOutlinedIcon />}
                        variant="contained"
                      >
                        {t(`profile.info.view_bc_driver_ID`)}
                      </Button>
                    </ListItem>
                  )}
                </List>
              ))}
            </TabPanel>
            <TabPanel index={2} value={value}>
              <List
                disablePadding
                subheader={
                  <ListSubheader component="div" id="nested-list-subheader">
                    {t('profile.info.downloadables')}
                  </ListSubheader>
                }
              >
                {driverResources.map((resource: ResourceType) => (
                  <ProfileResources
                    key={resource.fileKey}
                    resource={resource}
                  />
                ))}
              </List>
              <List
                disablePadding
                subheader={
                  <ListSubheader component="div" id="nested-list-subheader">
                    Apps
                  </ListSubheader>
                }
                sx={{ mt: 2 }}
              >
                <ListItemButton
                  href={t('profile.info.ios_driver_link')}
                  target="_blank"
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <AppleIcon />
                        <Typography variant="subtitle1">
                          {' '}
                          {t('profile.info.ios_driver_app')}{' '}
                        </Typography>
                      </Box>
                    }
                  />
                  <LaunchIcon color="primary" />
                </ListItemButton>
                <Divider component="li" variant="fullWidth" />
                <ListItemButton
                  href={t('profile.info.android_driver_link')}
                  target="_blank"
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <AndroidIcon />
                        <Typography variant="subtitle1">
                          {' '}
                          {t('profile.info.android_driver_app')}{' '}
                        </Typography>
                      </Box>
                    }
                  />
                  <LaunchIcon color="primary" />
                </ListItemButton>
              </List>
              <List
                disablePadding
                subheader={
                  <ListSubheader component="div" id="nested-list-subheader">
                    {t('profile.info.about')}
                  </ListSubheader>
                }
                sx={{ mt: 2 }}
              >
                <ListItemButton href="https://uride.co" target="_blank">
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {t('profile.company.title')}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body1">
                        {' '}
                        {t('uride.name')}{' '}
                      </Typography>
                    }
                  />
                  <LaunchIcon color="primary" />
                </ListItemButton>
                <Divider component="li" variant="middle" />
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {t('profile.company.email')}
                      </Typography>
                    }
                    secondary={
                      <Typography color="text.primary" variant="body1">
                        {t('uride.support_email')}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" variant="middle" />
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography color="text.disabled" variant="subtitle2">
                        {t('profile.company.phone')}
                      </Typography>
                    }
                    secondary={
                      <Typography color="text.primary" variant="body1">
                        {formatPhone(`+18077881716`)}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </TabPanel>
          </Grid>

          <Grid item sx={{ mt: 2 }} xs={12}>
            <Grid item textAlign="center" xs={12}>
              <Typography variant="caption">
                {__APP_VERSION__ ? `v${__APP_VERSION__}` : '0.0.0'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Upload Profile Picture Dialog */}
      <AvatarDialog
        isLargeScreen={isLargeScreen}
        open={open}
        refetch={refetch}
        setAlertEvent={setAlertEvent}
        setOpen={setOpen}
      />

      {/* Driver ID Dialog */}
      {driverData.status === 'active' && (
        <IdDialog data={driverData} open={open} setOpen={setOpen} />
      )}
      <CertificateDialog
        content={<CertificatePage data={selectedVehicle} />}
        onClose={handleCloseRCCModal}
        open={isRCCModalOpen}
        textToClose={t('common.close')}
        title={t('profile.info.record_check_certificate')}
      />
      <CertificateDialog
        content={<NBDriverIDPage data={selectedVehicle} />}
        onClose={handleCloseNBDriverIDModal}
        open={isNBDriverIDModalOpen}
        textToClose={t('common.close')}
        title={t('profile.info.view_bc_driver_ID')}
      />
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={3000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={6}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default Profile
